import React, { useEffect, useMemo, useState , useRef } from "react";
import { ReactComponent as LikeIcon } from "../../../assets/Icons/like.svg";
import { ReactComponent as ReplyIcon } from "../../../assets/Icons/reply.svg";
import { ReactComponent as FlagIcon } from "../../../assets/Icons/flag.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/Icons/delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Icons/edit.svg";
import Translate from "../../../Components/MultiLanguage/Translate";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { timeDifferenceWithCurrenttime } from "../../../utils/utils";
import { commentReport, deleteComment, fetchCommentReplies, likeDislikeComment } from "../service";
import PostComment from "./PostComment";
import { setReplyState } from "../../../Redux/Comments/commentSlice";
// import DOMPurify from "quill/formats/link";
import DOMPurify from 'dompurify';
import {formatMarkdownToHtml} from "../utils/helper";


const CommentItem = ({ data, postID, projectInfo, onDelete, onLike, onUpdateContent, activeReplyCommentId, setActiveReplyCommentId, isParentComment=true, type="comment" }) => {
  const user = useSelector((state) => state?.user?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const isReplyField = activeReplyCommentId === data.id;
  const [replies, setReplies] = useState([]);
  const [repliesPage, setRepliesPage] = useState(1);
  const [repliesLimit, setRepliesLimit] = useState(30); // default API limit
  const [hasMoreReplies, setHasMoreReplies] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [repliesCount, setRepliesCount] = useState(Number(data?.replies_count) || 0);
  const theme = projectInfo?.projectConfig?.config?.THEME;
  const commentCreatedDate = new Date(data?.created_at);
  const commentCreatedUnix = commentCreatedDate.getTime()/1000;
  // State to force update for 'Just now' -> '1 second ago'
  const [forceUpdate, setForceUpdate] = useState(false);
  const replyInputRef = useRef(null);

  useEffect(() => {
    const currentTimeUnix = Math.floor(Date.now() / 1000);
    const timeDifference = currentTimeUnix - commentCreatedUnix;
    let timer;
    if (timeDifference < 1) {
      timer = setTimeout(() => {
        setForceUpdate(f => !f);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [commentCreatedUnix, forceUpdate]);
  const dispatch = useDispatch();
  const editingState = useSelector((state) => state?.comment?.editingState);
  const appInfo = useMemo(() => ({
    projectDetails: projectInfo,
    accessToken: accessToken
  }), [projectInfo, accessToken]);

  useEffect(() => {
    const commentElements = document.querySelectorAll('.eachComment');
    commentElements.forEach((el) => {
      if (theme === 'LIGHT') {
        el.style.setProperty('--white-color', '#000000');
        el.style.setProperty('--inner-text-color', '#555555');
      } else {
        el.style.setProperty('--white-color', '#ffffff');
        el.style.setProperty('--inner-text-color', '#cccccc');
      }
    });
    return () => {
      commentElements.forEach((el) => {
        el.style.removeProperty('--white-color');
        el.style.removeProperty('--inner-text-color');
      });
    };
  }, [theme]);

  useEffect(() => {
  if (!isReplyField) return;

  function handleClickOutside(event) {
    if (replyInputRef.current && !replyInputRef.current.contains(event.target)) {
      setActiveReplyCommentId(null);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [isReplyField, setActiveReplyCommentId]);


  const mainReplyFieldHandler = () => {
    if (isReplyField) {
      setActiveReplyCommentId(null); // Close if already open
    } else {
      dispatch(setReplyState(null))
      setActiveReplyCommentId(data.id); // Open for this comment
    }
  };

  const editHandler = (id, type) => {
    setActiveReplyCommentId(null);
    dispatch(setReplyState({id: id, type: type}));
  }

  const reportHandler = () => {
    commentReport(appInfo, {
      comment_id: data?.id,
      reason: "others"
    }).then((response) => {
      if (response?.data?.success) {
        toast.success(response?.data?.message || <Translate textKey={'successfully_reported'}/>, {
          position: "top-center",
        });
      } else {
        toast.error(response?.data?.message || <Translate textKey={'already_reported'}/>, {
          position: "top-center",
        });
      }
    }).catch(() => {
      toast.error(<Translate textKey={'error_reporting_comment'}/>, {
        position: "top-center",
      });
    });
  };

  /**
   * Delete the current comment
   * @returns {Promise<void>}
   */
  const deleteCommentHandler = async () => {
    try {
      const response = await deleteComment(appInfo, data?.id, data?.post_id);
      if (response?.data?.success) {
        toast.success(response?.data?.message || "Comment deleted successfully", {
          position: "top-center",
        });
        if (onDelete) onDelete(); // Remove from array in parent
      } else {
        toast.error(response?.message || "Failed to delete comment", {
          position: "top-center",
        });
      }
    } catch (err) {
      toast.error("Error deleting comment", {
        position: "top-center",
      });
    }
  };

  /**
   * Load replies for the current comment
   * @returns {Promise<void>}
   */
  const loadReplies = async (page = 1, append = false) => {
    try {
      const response = await fetchCommentReplies(appInfo, data.id, { page });
      if (response?.data?.success) {
        const newReplies = response.data.data || [];
        setRepliesLimit(response.data.limit || 30);
        setHasMoreReplies(newReplies.length === (response.data.limit || 30));
        setReplies(prev => append ? [...prev, ...newReplies] : newReplies);
        setRepliesPage(page);
      }
    } catch (err) {
      console.error("Error loading replies", err);
    }
  };

  /**
   * Load replies when the reply field is opened with isReplyField as dependency
   */
  useEffect(() => {
    if (isReplyField) {
      loadReplies(1, false).catch((err) => console.error(err));
    }
  }, [isReplyField]);

  // 1. Add a handler to update replies
  const handleReplyPosted = () => {
    setRepliesCount((prev) => prev + 1);
    loadReplies().catch(() => {});
    setActiveReplyCommentId(null); // Hide reply input after posting
  };

  /**
   * Like or dislike the current comment
   * @param liked Boolean
   * @param id Number - comment or reply id
   * @returns {Promise<void>}
   */
  const likeHandler = async (liked, id) => {
    const toggleLike = await likeDislikeComment(appInfo, id, {
      liked: liked ? 0 : 1,
    });
    if (toggleLike?.data?.success) {
      const newLiked = liked ? 0 : 1;
      const newLikesCount = liked ? Number(data.likes_count) - 1 : Number(data.likes_count) + 1;
      if (onLike) onLike(id, newLiked, newLikesCount);
    }
  };

  const html = useMemo(() => {
    if (!data?.content) return "";
    return formatMarkdownToHtml(data.content);
  }, [data?.content]);

  return (
    <div className={`eachComment${!isParentComment ? ' replyComment' : ''}`}>
        <div className="containers">
          <div className="commentItem">
            <div className="left">
              {
                data.user?.user_image ? (
                    <div className="profileImageContainer">
                      <img src={data.user?.user_image} alt="Profile" />
                    </div>
                ):(
                    <span className="nameAvatar">{data.user?.user_name?.slice(0, 1)?.toUpperCase()}</span>
                )
              }
            </div>
            <div className="right">
              <div className="verticalCommentItemHead">
                 <h1 className="name">{data.user?.user_name}</h1>
                <span className="time">{timeDifferenceWithCurrenttime(commentCreatedUnix)}</span>

              </div>
             
              {data.content && <p className="comment">{
                <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(html)
                    }}
                />
              }</p>}
              {data?.image_url && (
                  <div className='comment-image-container'>
                    <img src={data?.image_url} alt={`Comment`}/>
                  </div>
              )}
              {data?.gif_url && (
                  <div className='comment-image-container'>
                    <img src={data?.gif_url} alt={`Comment`}/>
                  </div>
              )}
              <div className="actions">
              <span className="item" onClick={()=>{ likeHandler(data?.user_has_liked, data.id) }}>
                <span className="icon">
                    <LikeIcon style={data?.user_has_liked ? { fill: "red" } : {}} />
                </span>
                {data?.likes_count > 0 && data?.likes_count} {data?.likes_count >= 2 ? <Translate textKey={'likes'}/> : <Translate textKey={'like'}/>}
              </span>
                <span className="item" onClick={() => mainReplyFieldHandler()}>
                  <span className="icon">
                    <ReplyIcon />
                  </span>
                  {repliesCount > 0 && <>{repliesCount}</>} {repliesCount && repliesCount > 1 ? <Translate textKey={'replies'}/> : <Translate textKey={'reply'}/>}
              </span>
                {data?.user_id === Number(user) ? (
                    <span className="item" onClick={() => deleteCommentHandler()}>
                  <span className="icon">
                    <DeleteIcon />
                  </span>
                  <Translate textKey={'delete'}/>
                </span>
                ) : (
                    <span className="item" onClick={() => reportHandler()}>
                  <span className="icon">
                    <FlagIcon />
                  </span>
                  <Translate textKey={'report'}/>
                </span>
                )}
                {data?.user_id === Number(user) && (
                    <span className="item" onClick={() => editHandler(data.id, type)}>
                      <span className="icon">
                        <EditIcon/>
                      </span>
                      <Translate textKey={'edit'}/>
                    </span>
                )}
              </div>
            {isParentComment && (
                isReplyField ? (
                  <div ref={replyInputRef}>
                    <PostComment
                      key={`reply-input-${data.id}`}
                      postId={postID}
                      initialContent={""}
                      onCommentPosted={handleReplyPosted}
                      projectInfo={projectInfo}
                      parentCommentId={data.id}
                    />
                  </div>
                ) : (editingState?.id === data.id && editingState?.type === 'comment') ? (
                    <PostComment
                        key={`edit-comment-${data.id}`}
                        hideMedia={true}
                        isEditComment={true}
                        initialContent={data.content}
                        postId={postID}
                        onCommentPosted={(res, content) => {
                          if (res.status === 200 && res.data.success) {
                            dispatch(setReplyState(null))
                            onUpdateContent(res.data.data.id, content);
                          } else {
                            toast.error(res?.message || res.data.message || "Failed to edit reply", {
                              position: "top-center",
                            })
                          }
                        }}
                        projectInfo={projectInfo}
                        parentCommentId={data.id}
                    />
                ) : null
              )}
              {replies?.filter(re => {
                return re.comment_id === data.id
              }).map((reply) => {
                return <React.Fragment key={reply.id}>
                  <CommentItem
                      data={reply}
                      projectInfo={projectInfo}
                      onLike={(id, newLiked, newLikesCount)=>{
                        setReplies((prevReplies) =>
                            prevReplies.map((r) =>
                                r.id === id ? { ...r, user_has_liked: newLiked, likes_count: newLikesCount } : r
                            )
                        );
                      }}
                      onUpdateContent={(id, newContent) => {
                        setReplies((prevReplies) => {
                          return prevReplies.map((r) =>
                              r.id === id ? { ...r, content: newContent } : r
                          );
                        })
                      }}
                      onDelete={() => {
                        setReplies((prevReplies) => prevReplies.filter((r) => r.id !== reply.id));
                        setRepliesCount((prev) => prev - 1);
                      }}
                      type="reply"
                      activeReplyCommentId={activeReplyCommentId}
                      setActiveReplyCommentId={setActiveReplyCommentId}
                      isParentComment={false}
                  />
                  {editingState?.id == reply.id && editingState?.type === 'reply' && (
                      <PostComment
                          id = {reply.id}
                          key={`edit-reply-${reply.id}`}
                          hideMedia={true}
                          isEditComment={true}
                          initialContent={reply.content}
                          postId={reply.post_id}
                          onCommentPosted={(res, content) => {
                            if (res.status === 200 && res.data.success) {
                              setReplies((prevReplies) => prevReplies.map((r) => r.id === reply.id ? { ...r, content: content } : r));
                              dispatch(setReplyState(null))
                            } else {
                              toast.error(res?.message || res.data.message || "Failed to edit reply", {
                                position: "top-center",
                              })
                            }
                          }}
                          projectInfo={projectInfo}
                          parentCommentId={reply.comment_id}
                      />
                  )}
                  {/* Reply UI for reply */}
                  {activeReplyCommentId === reply.id && (!editingState || editingState.id !== reply.id) && (
                     <div ref={replyInputRef}>
                        <PostComment
                            key={`reply-input-${reply.id}`}
                            postId={reply.post_id}
                            onCommentPosted={handleReplyPosted}
                            projectInfo={projectInfo}
                            parentCommentId={reply.comment_id}
                        />
                    </div>
                  )}
                </React.Fragment>
              })}
              {hasMoreReplies && (
                <div style={{marginTop:16, display:'flex', justifyContent:'center'}}>
                  <button onClick={() => loadReplies(repliesPage + 1, true)}>
                    Load More Replies
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

export default CommentItem;
